import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItem,
  Button
} from '@mui/material'
import Grid from '@mui/material/Grid'
import AvatarChip from 'components/atoms/AvatarChip'
import ProfileAvatar from 'components/atoms/ProfileAvatar'
import { ActionText } from 'components/atoms/Text'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { deleteRequest } from 'utils/api'
import { COHORT_USERS } from 'services/constants'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'
import { useLocation } from 'react-router-dom'

const UsersIconPopupList = ({ users, parentKey, popupTitle, cohortName }) => {
  const [raterDialogOpen, setRaterDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [requestError, setRequestError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [localUsers, setLocalUsers] = useState(users)
  const [successMessage, setSuccessMessage] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const location = useLocation()
  const isCompanyCohortPage = location.pathname.includes('company-cohort')
  const { t } = useTranslation()

  const handleRemoveClick = user => {
    setSelectedUser(user)
    setRequestError(null)
    setConfirmDialogOpen(true)
  }

  const handleConfirmDialogClose = async confirmed => {
    if (confirmed) {
      setLoading(true)
      const res = await deleteRequest(
        `${COHORT_USERS}`,
        {},
        {
          cohortId: parentKey,
          userId: selectedUser.id
        }
      )

      if (res.status === 204) {
        setLocalUsers(prev => prev.filter(user => user.id !== selectedUser.id))
        setSuccessMessage(
          `${selectedUser.firstName} ${selectedUser.lastName} ${t('company.removeUserFromCohortSuccess')}`
        )
        setSnackbarOpen(true)
        setConfirmDialogOpen(false)
        setSelectedUser(null)
        setRequestError(null)
      } else {
        setRequestError(true)
      }

      setLoading(false)
    } else {
      setConfirmDialogOpen(false)
    }
  }

  return (
    <>
      <Grid container gap={2}>
        {localUsers.slice(0, 3).map(rater => (
          <ProfileAvatar
            key={`user-avatar-${parentKey}-${rater.id}`}
            size='small'
            user={rater}
          />
        ))}
        {localUsers.length > 3 && (
          <ActionText variant='body2' onClick={() => setRaterDialogOpen(true)}>
            + {localUsers.length - 3} {t('common.more')}
          </ActionText>
        )}
      </Grid>
      <Dialog open={raterDialogOpen} onClose={() => setRaterDialogOpen(false)}>
        <DialogTitle>
          {popupTitle}
          <IconButton
            sx={{ float: 'right', paddingTop: '2px' }}
            onClick={() => setRaterDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minWidth: '300px' }}>
          {localUsers.map(rater => (
            <ListItem
              key={`popup-list-${parentKey}-${rater.id}`}
              sx={{ paddingY: '2px', paddingX: 0 }}>
              <AvatarChip user={rater} variant='filled' />
              {!isCompanyCohortPage && (
                <Button
                  color='muted'
                  size='small'
                  onClick={() => handleRemoveClick(rater)}>
                  {t('common.remove')}
                </Button>
              )}
            </ListItem>
          ))}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <CommonDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        titleText={t('company.removecohortUser')}
        notificationText={
          selectedUser
            ? t('company.removeUserFromCohort')
                .replace('{firstName}', selectedUser.firstName)
                .replace('{lastName}', selectedUser.lastName)
                .replace('{cohortName}', cohortName)
            : ''
        }
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        confirmValue={1}
        cancelValue={0}
        showCloseButton>
        {requestError && <RequestRetryAlert />}
      </CommonDialog>

      {/* Overlay Loading */}
      <OverlayLoading open={loading} />

      <NotificationSnackBar
        open={snackbarOpen}
        success
        successText={successMessage}
        setOpen={setSnackbarOpen}
      />
    </>
  )
}

export default UsersIconPopupList
